import React, { useCallback, useState } from "react"
import { useMutation } from "@apollo/client/react"
import { toast } from "sonner"
import { ToggleResourceEngagementMutation } from "@love-irish/graphql-api"
import { useSession } from "@love-irish/application-session-provider"

// Where the "View" toast action sends the user. The saved list lives at /my/saved.
const SAVED_PATH = "/my/saved"

export interface UseSaveToggleOptions {
  /** GraphQL resource type, e.g. "Item::CodexRecord", "Collection". */
  resourceType: string
  /** The resource's global id (gid). */
  resourceId: string
  /** Display name, used in the toast copy (falls back to a generic noun). */
  name?: string
  /** Server-sanitized, plain-text name (markdown already stripped). Preferred for the toast so it does
   *  no per-toast markdown parsing; falls back to `name`. */
  sanitizedName?: string
  /** Initial saved state, from the server. */
  initialSaved?: boolean
  /** Called when a logged-out user attempts to save — e.g. open an upsell modal. */
  onRequireAuth?: () => void
}

export interface UseSaveToggleResult {
  /** Current (optimistic) saved state. */
  saved: boolean
  /** Whether the toggle mutation is in flight. */
  loading: boolean
  /** Toggle saved/unsaved. No-ops into `onRequireAuth` when logged out. */
  toggle: () => Promise<void>
}

/**
 * App-wide "save/unsave a resource" logic: the ProfileEngagement(:saved) toggle mutation,
 * optimistic state with rollback on error, and consistent success/error toasts. UI-agnostic —
 * pair it with any button or menu item. Logged-out attempts call `onRequireAuth` instead of
 * mutating, so each surface supplies its own auth prompt.
 *
 * ```tsx
 * const { saved, loading, toggle } = useSaveToggle({
 *   resourceType: item.type,
 *   resourceId: item.gid,
 *   name: item.name,
 *   initialSaved: item.saved,
 *   onRequireAuth: () => setUpsellOpen(true),
 * })
 * ```
 */
export function useSaveToggle({
  resourceType,
  resourceId,
  name,
  sanitizedName,
  initialSaved = false,
  onRequireAuth,
}: UseSaveToggleOptions): UseSaveToggleResult {
  const { loggedIn } = useSession()
  const [saved, setSaved] = useState(initialSaved)
  const [toggleEngagement, { loading }] = useMutation(
    ToggleResourceEngagementMutation
  )

  const toggle = useCallback(async () => {
    if (!loggedIn) {
      onRequireAuth?.()
      return
    }

    // Optimistic flip; revert on error.
    const next = !saved
    setSaved(next)

    // Names can contain simple markdown (e.g. "Lá Fhéile Pádraig _(St. Patrick's Day)_"). Use the
    // server-sanitized plain name so the toast does no per-toast markdown parsing.
    const displayName = sanitizedName ?? name
    const label: React.ReactNode = displayName ? (
      <>
        &ldquo;<strong className="font-sans font-bold">{displayName}</strong>
        &rdquo;
      </>
    ) : (
      "this"
    )

    try {
      await toggleEngagement({
        variables: { resourceType, resourceId, engagementType: "saved" },
      })

      if (next) {
        toast.success(<>Saved {label}</>, {
          description: "Find it anytime in your saved items.",
          action: {
            label: "View",
            onClick: () => window.location.assign(SAVED_PATH),
          },
        })
      } else {
        toast(<>Removed {label} from saved</>)
      }
    } catch (err) {
      setSaved(!next)
      toast.error("Couldn't update your saved items. Please try again.")
      // eslint-disable-next-line no-console
      console.error("Failed to toggle save:", err)
    }
  }, [
    loggedIn,
    saved,
    name,
    sanitizedName,
    resourceType,
    resourceId,
    toggleEngagement,
    onRequireAuth,
  ])

  return { saved, loading, toggle }
}

export default useSaveToggle
